import { SelectionType } from "@components/atoms/Filters/types";
import { VDBText } from "@components/atoms";
import React, { useState } from "react";
import { useTypographyStore } from "@redux/slices/typographySlice";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import Tooltip from "@components/atoms/Tooltip";

type SelectedItemsTitleProps = {
  selectionType?: string;
  selectedItems?: any;
  reverseSelectedItemTitle?: boolean;
};
const SelectedItemsTitle = ({
  selectionType,
  selectedItems,
  reverseSelectedItemTitle,
}: SelectedItemsTitleProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const maxVisibleItems = 2;
  const totalItems = selectedItems?.length ?? 0;
  const remainingCount =
    totalItems > maxVisibleItems ? totalItems - maxVisibleItems : 0;

  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);

  const valueToDisplay = selectedItems
    ?.slice(0, maxVisibleItems)
    .map((item: any) => t(item?.label))
    .join(", ");

  const tooltipContent = (
    <VDBText
      type={typography["Minor-R"]}
      color={theme["core-01"]}
      textAlign="center"
    >
      {selectedItems?.map((item: any) => t(item?.label)).join(", ")}
    </VDBText>
  );

  const renderWithTooltip = (children: React.ReactNode, tooltipId: string) => {
    if (theme.isLargeScreen) {
      return (
        <TooltipButton
          renderComponent={tooltipContent}
          enableTooltip={selectedItems?.length > 2 ? true : false}
          position="top"
        >
          {children}
        </TooltipButton>
      );
    }
    return (
      <Tooltip
        isOpen={openTooltipId === tooltipId}
        setIsOpen={(isOpen) => setOpenTooltipId(isOpen ? tooltipId : null)}
        trigger={() => children}
        showOnPress={selectedItems?.length > 2 ? true : false}
        tooltipBackgroundColor={theme["core-06"]}
        popoverBackdropColor="transparent"
        placement="top"
        alignment="center"
        popoverContentProps={{ width: 240 }}
      >
        {tooltipContent}
      </Tooltip>
    );
  };

  if (selectionType === SelectionType.range)
    return (
      <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
        From:{" "}
        <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
          {reverseSelectedItemTitle
            ? t(selectedItems[selectedItems.length - 1]?.label)
            : t(selectedItems[0]?.label)}
        </VDBText>{" "}
        to{" "}
        <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
          {reverseSelectedItemTitle
            ? t(selectedItems[0]?.label)
            : t(selectedItems[selectedItems.length - 1]?.label)}
        </VDBText>
      </VDBText>
    );

  if (selectionType === SelectionType.single)
    return (
      <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
        Selected:{" "}
        <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
          {t(selectedItems[0]?.label)}
        </VDBText>
      </VDBText>
    );
  if (selectionType === SelectionType.multiple)
    return (
      <SelectedMultipleRow>
        <SelectedNoShrink>
          <VDBText
            type={typography["Minor-R"]}
            color={theme["core-06"]}
            numberOfLines={1}
          >
            Selected:{" "}
          </VDBText>
        </SelectedNoShrink>
        <SelectedValuesClamp>
          {renderWithTooltip(
            <VDBText
              type={typography["Minor-M"]}
              color={theme["core-05"]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {valueToDisplay}
            </VDBText>,
            "values"
          )}
        </SelectedValuesClamp>
        {remainingCount > 0 && (
          <SelectedNoShrink style={{ marginLeft: 4 }}>
            {renderWithTooltip(
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={1}
              >
                {`+ ${remainingCount} more`}
              </VDBText>,
              "remaining"
            )}
          </SelectedNoShrink>
        )}
      </SelectedMultipleRow>
    );
};

const SELECTED_VALUES_MAX_WIDTH_PCT = (174 / (54 + 174)) * 100;

const SelectedMultipleRow = styled.View`
  flex-direction: row;
  align-items: center;
  align-self: flex-end;
  flex-shrink: 1;
  min-width: 0px;
  max-width: 75%;
`;

const SelectedNoShrink = styled.View`
  flex-shrink: 0;
`;

const SelectedValuesClamp = styled.View`
  flex-shrink: 1;
  min-width: 0px;
  max-width: ${SELECTED_VALUES_MAX_WIDTH_PCT}%;
`;

export default SelectedItemsTitle;
